import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import axios from "axios";
import ReportHero from "./ReportHero";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";

const SystemLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [activityFilter, setActivityFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const { token } = getAuthCredentials();

  const fetchLogs = async (date) => {
    if (!date) return;
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/reports/view-log-report`,
        { date },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const sortedLogs = (response.data.report || []).sort(
        (a, b) => new Date(b.dateTime) - new Date(a.dateTime)
      );
      setLogs(sortedLogs);
      setFilteredLogs(sortedLogs);
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSearchDate(date);
    setActivityFilter(""); // Reset activity filter when date changes
    fetchLogs(date);
  };

  const handleActivityFilterChange = (e) => {
    const selectedActivity = e.target.value;
    setActivityFilter(selectedActivity);
    
    if (selectedActivity === "") {
      setFilteredLogs(logs);
    } else {
      const filtered = logs.filter(log => 
        log.activity.toLowerCase().includes(selectedActivity.toLowerCase())
      );
      setFilteredLogs(filtered);
    }
  };

  // Get unique activity types for the filter dropdown
  const getUniqueActivities = () => {
    const activities = logs.map(log => {
      // Extract the main activity type (text before parentheses)
      const match = log.activity.match(/^([^(]+)/);
      return match ? match[1].trim() : log.activity;
    });
    return [...new Set(activities)].sort();
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setSearchDate(today);
    fetchLogs(today);
  }, []);

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <TitleSetter title="Audit Trail Logs" />
      <ReportHero />
      <Typography variant="h5" textAlign="center" gutterBottom>
        System Audit Trail / Activity Log
      </Typography>
      <Box sx={{ marginBottom: 3 }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Search by Date"
              type="date"
              value={searchDate}
              onChange={handleDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Activity</InputLabel>
              <Select
                value={activityFilter}
                label="Filter by Activity"
                onChange={handleActivityFilterChange}
              >
                <MenuItem value="">
                  <em>All Activities</em>
                </MenuItem>
                {getUniqueActivities().map((activity) => (
                  <MenuItem key={activity} value={activity}>
                    {activity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Paper elevation={3} sx={{ padding: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredLogs.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time</TableCell>
                <TableCell>User Name / ID</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Values</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <TableCell>
                    {new Date(log.dateTime).toLocaleString("en-US", {
                      timeZone: "Asia/Manila",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                    })}
                  </TableCell>
                  <TableCell>{log.userNameId}</TableCell>
                  <TableCell>{log.activity}</TableCell>
                  <TableCell>{log.itemName || "N/A"}</TableCell>
                  <TableCell>{log.values}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body1" textAlign="center" color="textSecondary">
            {logs.length === 0 
              ? "No logs found for the selected date." 
              : "No logs match the selected activity filter."}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default SystemLogs;
